import { useId, useState, type FormEvent } from 'react';
import { KeyRound, Pencil, UserRound } from 'lucide-react';
import { CAPABILITIES, PERMISSIONS, accessLevelFor } from '@quds/shared';
import { useAuth, useCurrentUser } from '@/auth/AuthContext';
import { Badge, Button, Card, CardBody, CardHeader, DescriptionList, FormField, Input, Modal, fieldDescribedBy, useToast } from '@/components/ui';
import { RecordStatusBadge } from '@/features/pharmacies/components/StatusBadges';
import { ApiError, errorMessage } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { ChangePasswordModal } from './ChangePasswordModal';

/** The signed-in admin's own account. Role and status are managed by a Super Admin in Admin Users. */
export function MyAccountCard() {
  const user = useCurrentUser();
  const [modal, setModal] = useState<'password' | 'name' | null>(null);
  const capabilities = CAPABILITIES.map((capability) => ({ capability, level: accessLevelFor(user.permissions, capability) }));

  return (
    <Card>
      <CardHeader
        title="My Account"
        icon={<UserRound size={16} aria-hidden="true" />}
        description="Your profile and sign-in. Permissions come from your role and are enforced by the API"
        actions={
          <Button variant="secondary" size="sm" leftIcon={<KeyRound size={14} aria-hidden="true" />} onClick={() => setModal('password')}>
            Change Password
          </Button>
        }
      />
      <CardBody className="space-y-4">
        <DescriptionList
          items={[
            {
              label: 'Display Name',
              value: (
                <span className="flex items-center gap-1.5">
                  <span className="min-w-0 break-words">{user.displayName}</span>
                  <Button variant="ghost" size="icon-sm" aria-label="Edit display name" onClick={() => setModal('name')}>
                    <Pencil size={14} aria-hidden="true" />
                  </Button>
                </span>
              ),
            },
            { label: 'Email', value: user.email },
            { label: 'Role', value: user.role.name },
            { label: 'Status', value: <RecordStatusBadge status={user.status} /> },
            { label: 'Last Login', value: user.lastLoginAt ? formatDateTime(user.lastLoginAt) : '—' },
            { label: 'Password Last Changed', value: formatDateTime(user.passwordChangedAt) },
          ]}
        />
        <div>
          <p className="text-xs font-medium text-ink-muted">Permissions ({user.role.name})</p>
          <ul className="mt-1.5 divide-y divide-line rounded-md border border-line" aria-label="Your permissions">
            {capabilities.map(({ capability, level }) => (
              <li
                key={capability.label}
                className="flex items-center justify-between gap-3 px-3 py-1.5 text-[13px]"
                title={[capability.view, ...(capability.manage ?? [])].map((p) => `${p}: ${PERMISSIONS[p]}`).join('\n')}
              >
                <span className="text-ink-secondary">{capability.label}</span>
                <span className="flex shrink-0 items-center gap-1.5">
                  {level === 'Manage' && capability.futureOperation && <span className="text-[11px] text-ink-muted">after production linking</span>}
                  <Badge tone={level === 'Manage' ? 'primary' : level === 'View' ? 'neutral' : 'muted'}>{level}</Badge>
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-1.5 text-xs text-ink-muted">Your role is assigned by a Super Admin. The API enforces these permissions on every request.</p>
        </div>
      </CardBody>
      {modal === 'password' && <ChangePasswordModal onClose={() => setModal(null)} />}
      {modal === 'name' && <EditDisplayNameModal current={user.displayName} onClose={() => setModal(null)} />}
    </Card>
  );
}

function EditDisplayNameModal({ current, onClose }: { current: string; onClose: () => void }) {
  const { updateDisplayName } = useAuth();
  const { notify } = useToast();
  const fieldId = useId();
  const [value, setValue] = useState(current);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const name = value.trim();
    if (name.length < 2) {
      setError('Enter a display name (at least 2 characters).');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await updateDisplayName(name);
      notify({ tone: 'success', title: 'Display name updated' });
      onClose();
    } catch (err) {
      setError(err instanceof ApiError && err.fieldIssues[0] ? err.fieldIssues[0].message : errorMessage(err, 'Could not update the display name.'));
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title="Edit Display Name"
      description="Shown in the console and the audit trail"
      icon={<UserRound size={18} aria-hidden="true" />}
      dismissible={!saving}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" form="display-name-form" loading={saving}>
            Save Changes
          </Button>
        </>
      }
    >
      <form id="display-name-form" noValidate onSubmit={onSubmit}>
        <FormField label="Full Name" htmlFor={fieldId} required error={error ?? undefined}>
          <Input
            id={fieldId}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            maxLength={120}
            invalid={!!error}
            aria-describedby={fieldDescribedBy(fieldId, { error: error ?? undefined })}
            autoFocus
          />
        </FormField>
      </form>
    </Modal>
  );
}
